/**
 * Relative time, in the style the pipeline screens use throughout.
 *
 * Absolute dates are the wrong unit here: what a reviewer wants to know is
 * whether something happened recently enough to still be true, and "3d ago"
 * answers that instantly where "16 Aug" needs mental arithmetic. Anything
 * beyond a month falls back to a date, because at that range the exact day
 * starts to matter again.
 */
export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";

  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";

  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return "just now";

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.round(hours / 24);
  if (days < 31) return `${days}d ago`;

  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function absoluteTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** 2,400 rather than 2400 — headcounts are read, not computed. */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return value.toLocaleString();
}

/**
 * `fw_provider` → `Fw provider`.
 *
 * Enrichment keys are chosen by agents, so the UI cannot rely on a fixed list.
 * Known acronyms are spelled correctly; everything else gets sentence case,
 * which is wrong far less often than leaving raw snake_case on screen.
 */
const KNOWN_TERMS: Record<string, string> = {
  fw: "Financial wellbeing",
  eap: "EAP",
  hr: "HR",
  esg: "ESG",
  ceo: "CEO",
  cfo: "CFO",
  url: "URL",
};

export function humaniseKey(key: string): string {
  const words = key.split(/[_\-\s]+/).filter(Boolean);
  if (words.length === 0) return key;

  return words
    .map((word, index) => {
      const known = KNOWN_TERMS[word.toLowerCase()];
      if (known) return index === 0 ? known : known.toLowerCase();
      return index === 0
        ? word.charAt(0).toUpperCase() + word.slice(1)
        : word.toLowerCase();
    })
    .join(" ");
}

/** Render an enrichment fact's value, which may be any JSON shape. */
export function formatFactValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value.toLocaleString();
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return value.map((item) => formatFactValue(item)).join(", ");
  }
  return JSON.stringify(value);
}
